/**
 * Seguidor del avance de una operación masiva de catálogo (edición masiva de
 * insumos, actualización de precios, importaciones).
 *
 * El avance viaja por WebSocket, pero ese socket puede no recibir nada: en
 * producción hay dos servidores detrás del balanceador y el trabajo puede correr
 * en la instancia donde el navegador NO tiene su socket, además de las caídas
 * normales de red, las suspensiones del equipo y los F5 a media corrida. Sin el
 * respaldo por polling el modal se queda girando para siempre.
 *
 * El backend mantiene una llave en Redis mientras el proceso vive
 * (Utils/estadoProceso.utils.js): "llave ausente" = terminó o murió.
 *
 * Es la misma coreografía que PreNomina.js resolvió para el cálculo de nómina,
 * extraída para no reescribirla en cada pantalla.
 *
 * Uso típico en un componente de clase:
 *
 *   componentDidMount() {
 *     this.seguidor = new SeguidorProcesoMasivo({
 *       empresaId: this.props.empresaId,
 *       headersToken: this.props.headersToken,
 *       onProgreso: (p) => this.setState({ progreso: p }),
 *       onFin: (resumen) => { ... },
 *       onError: (msg) => { ... },
 *     })
 *     this.seguidor.escuchar()
 *   }
 *   componentWillUnmount() { this.seguidor.detener() }
 */

import axios from 'axios'

const TIPO_PROCESO = 'insumosMasivo'
const INTERVALO_RESPALDO_MS = 10000

// El endpoint también devuelve null cuando Redis no responde, así que una sola
// lectura vacía no basta para dar el proceso por terminado: apagaría el modal a
// media corrida.
const LECTURAS_VACIAS_PARA_TERMINAR = 2

class SeguidorProcesoMasivo {
  constructor({ empresaId, headersToken, onProgreso, onFin, onError }) {
    this.empresaId = empresaId
    this.headersToken = headersToken
    this.onProgreso = onProgreso || (() => {})
    this.onFin = onFin || (() => {})
    this.onError = onError || (() => {})

    this._manejador = null
    this._intervalo = null
    this._lecturasVacias = 0
    // El WebSocket y el respaldo pueden llegar los dos al final; el resultado se
    // entrega UNA vez.
    this._terminado = false
    // Id de la corrida que esta pantalla está siguiendo. null = ninguna.
    this._procesoId = null
    // Evento final que llegó por WebSocket ANTES de que nos dijeran a qué
    // corrida seguir. Ver `seguir()`.
    this._finAdelantado = null
  }

  /**
   * Engancha el WebSocket. Barato: no consulta nada y no arranca el respaldo.
   * Se llama al montar la pantalla. Idempotente.
   *
   * El WebSocket ya viene filtrado por usuario desde el servidor, así que aquí no
   * hay riesgo de reaccionar al trabajo de alguien más — pero sí al de otra
   * pantalla del mismo usuario, y de eso se encarga el `procesoId`.
   */
  escuchar() {
    if (this._manejador) return

    this._manejador = (event) => {
      const msg = event.detail || {}
      const esFinal = msg.evento === 'masivo_fin' || msg.evento === 'masivo_error'

      if (!this._procesoId) {
        // Todavía no sabemos qué corrida seguir porque la respuesta HTTP no ha
        // aterrizado. Un proceso corto puede terminar en ese hueco, así que el
        // evento final se guarda; `seguir()` lo entrega si resulta ser el suyo.
        if (esFinal && msg.procesoId) this._finAdelantado = msg
        return
      }

      // De otra corrida (otra pantalla, o una anterior de ésta).
      if (msg.procesoId && msg.procesoId !== this._procesoId) return

      if (msg.evento === 'masivo_progreso' || msg.evento === 'masivo_inicio') {
        this._lecturasVacias = 0
        this.onProgreso(this._normalizar(msg))
        return
      }

      if (msg.evento === 'masivo_fin') this._terminar(msg.resumen || {})
      if (msg.evento === 'masivo_error') this._fallar(msg.error)
    }

    window.addEventListener('proceso_masivo', this._manejador)
  }

  /**
   * Empieza a seguir la corrida `procesoId`: arranca el respaldo por polling. Se
   * llama cuando ESTA pantalla la lanzó (o cuando `consultarEnCurso` encontró
   * una propia viva).
   *
   * El respaldo no arranca en `escuchar` a propósito: sin un proceso propio en
   * curso sería una consulta cada 10 s sin motivo, en todas las pantallas que
   * usan esto.
   */
  seguir(procesoId = null) {
    this._procesoId = procesoId
    this._terminado = false
    this._lecturasVacias = 0

    // ¿El proceso ya había terminado antes de que llegara la respuesta HTTP? El
    // id descarta un final viejo: sólo se entrega si es de ESTA corrida.
    const adelantado = this._finAdelantado
    this._finAdelantado = null
    if (adelantado && procesoId && adelantado.procesoId === procesoId) {
      if (adelantado.evento === 'masivo_fin') return this._terminar(adelantado.resumen || {})
      return this._fallar(adelantado.error)
    }

    if (this._intervalo) return
    this._intervalo = setInterval(() => this._revisarRespaldo(), INTERVALO_RESPALDO_MS)
  }

  /** Suelta el listener y el respaldo. Llamar siempre en componentWillUnmount. */
  detener() {
    if (this._manejador) window.removeEventListener('proceso_masivo', this._manejador)
    this._manejador = null
    this._pararRespaldo()
  }

  /**
   * ¿Hay un proceso PROPIO vivo ahora mismo? Sirve al montar la pantalla: si el
   * usuario recargó el navegador a media corrida, el estado local arranca en
   * blanco y el modal no aparecería.
   *
   * Si encuentra uno, ya deja el respaldo andando. Devuelve el último avance
   * conocido, o null.
   */
  async consultarEnCurso() {
    const datos = await this._consultarEstado()
    // El servidor devuelve `estado: null` con `deOtroUsuario: true` cuando sí hay
    // algo corriendo pero lo lanzó otra persona: no es nuestro modal.
    if (!datos || !datos.estado) return null

    this.seguir(datos.estado.procesoId || null)
    return this._normalizar(datos.estado)
  }

  _normalizar(payload) {
    return {
      procesados: payload.procesados || 0,
      total: typeof payload.total === 'number' ? payload.total : null,
      detalle: payload.detalle || null,
      operacion: payload.operacion || null,
    }
  }

  _pararRespaldo() {
    if (this._intervalo) clearInterval(this._intervalo)
    this._intervalo = null
    this._lecturasVacias = 0
  }

  async _consultarEstado() {
    try {
      // Ojo: `procesoMasivo`, no `procesos` — ese último es el Dashboard de
      // Procesos, un módulo que no tiene nada que ver con esto.
      const res = await axios.post(
        'procesoMasivo/estadoProceso',
        { empresaId: this.empresaId, tipo: TIPO_PROCESO },
        { headers: this.headersToken },
      )
      if (res.data.success !== 2) return null
      return res.data
    } catch {
      // Error de red: se reintenta en el siguiente tick.
      return null
    }
  }

  async _revisarRespaldo() {
    if (this._terminado) return

    const datos = await this._consultarEstado()
    if (!datos) return

    const esNuestro = (registro) =>
      !!registro && (!this._procesoId || !registro.procesoId || registro.procesoId === this._procesoId)

    if (datos.estado) {
      if (!esNuestro(datos.estado)) {
        // Lo que está corriendo ya es otra corrida (otra pestaña del mismo
        // usuario). La nuestra terminó y su resultado quedó pisado.
        return this._terminar(null)
      }
      // Sigue vivo. Se aprovecha para sincronizar el avance, por si el
      // WebSocket tampoco está entregando los eventos de progreso.
      this._lecturasVacias = 0
      this.onProgreso(this._normalizar(datos.estado))
      return
    }

    // Hay un proceso corriendo, pero es de otra persona: el nuestro ya no está.
    // Su resultado puede seguir ahí (el servidor lo filtra aparte); si no, se
    // cierra el modal en seco en vez de anunciar números que no existen.
    if (datos.deOtroUsuario) {
      return this._terminar(esNuestro(datos.resultado) ? datos.resultado : null)
    }

    this._lecturasVacias += 1
    if (this._lecturasVacias < LECTURAS_VACIAS_PARA_TERMINAR) return

    // El resultado sobrevive al fin del proceso justamente para este camino.
    this._terminar(esNuestro(datos.resultado) ? datos.resultado : null)
  }

  _terminar(resumen) {
    if (this._terminado) return
    this._terminado = true
    this._procesoId = null
    this._pararRespaldo()
    // `null` = terminó pero no hay resultado que reportar (el proceso murió, o
    // lo que está corriendo ya es otra corrida). Quien lo use debe cerrar el
    // modal sin anunciar cifras inventadas.
    this.onFin(resumen)
  }

  _fallar(mensaje) {
    if (this._terminado) return
    this._terminado = true
    this._procesoId = null
    this._pararRespaldo()
    this.onError(mensaje || 'Error en el proceso')
  }
}

export default SeguidorProcesoMasivo
